import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Text, TextInput, TouchableOpacity, View } from "react-native";
import { useTranslation } from "react-i18next";

import { useDevice } from "../_layout";

import Colors from "../../Resources/Color";
import { withAlpha } from "../utils/colorUtils";

// Match selection styling used by pills in ExerciseSearchFloating.js / ExerciseManualPickerModal.js
const PILL_COLORS = {
  modifier: {
    background: "rgba(156, 156, 156, 0.12)",
    text: Colors.darkgrayColor,
    border: "rgba(156, 156, 156, 0.20)",
  },
};

const PillButton = ({
  label,
  isMobile,
  selected,
  onPress,
  onPressIn,
  isAdd = false,
  isDisabled = false,
  forceHover = false,
  preventDefaultOnPress = false,
}) => {
  const selectedColors = PILL_COLORS.modifier;
  const [isHovered, setIsHovered] = useState(false);

  const showHover = (isHovered || forceHover) && !selected && !isDisabled;
  const backgroundColor = selected
    ? selectedColors.background
    : showHover
    ? withAlpha(selectedColors.background, 0.65)
    : Colors.whiteColor;
  const textColor = selected
    ? selectedColors.text
    : showHover
    ? withAlpha(selectedColors.text, 0.82)
    : Colors.darkgrayColor;

  return (
    <TouchableOpacity
      onPress={isDisabled ? undefined : onPress}
      onPressIn={isDisabled ? undefined : onPressIn}
      delayPressIn={preventDefaultOnPress ? 90 : 0}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={(e) => {
        if (!preventDefaultOnPress) return;
        e?.preventDefault?.();
      }}
      style={{
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: selected
          ? Colors.darkgrayColor
          : showHover
          ? selectedColors.border
          : Colors.borderColor,
        backgroundColor,
        outlineStyle: "none",
        cursor: isDisabled ? "default" : "pointer",
        opacity: isDisabled ? 0.55 : 1,
        maxWidth: "100%",
        flexShrink: 1,
        alignSelf: "flex-start",
      }}
    >
      <Text
        style={{
          fontSize: isAdd ? 14 : 12,
          fontWeight: "500",
          color: textColor,
          lineHeight: 16,
          flexShrink: 1,
          flexWrap: "wrap",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

const CreateModifierComposerPill = React.memo(
  ({
    isMobile,
    value,
    onChangeText,
    placeholder,
    inputRef,
    onSubmit,
    onCancel,
    canSubmit,
    cancelLabel,
    saveLabel,
    autoFocus,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (!isFocused) return;
      if (typeof document === "undefined") return;

      const handler = (e) => {
        const key = e?.key;
        if (key !== "Escape" && key !== "Esc") return;
        e?.preventDefault?.();
        e?.stopPropagation?.();
        onCancel?.();
      };

      document.addEventListener("keydown", handler, true);
      return () => {
        document.removeEventListener("keydown", handler, true);
      };
    }, [isFocused, onCancel]);

    const borderColor = isFocused
      ? withAlpha(Colors.darkgrayColor, 0.5)
      : isHovered
      ? withAlpha(Colors.darkgrayColor, 0.35)
      : Colors.borderColor;

    const handleKey = useCallback(
      (e) => {
        e?.stopPropagation?.();
        const key = e?.nativeEvent?.key ?? e?.key;

        if (key === "Escape") {
          e?.preventDefault?.();
          onCancel?.();
          return;
        }

        if (key === "Enter") {
          e?.preventDefault?.();
          if (canSubmit) onSubmit?.();
        }
      },
      [canSubmit, onCancel, onSubmit]
    );

    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          borderRadius: 10,
          borderWidth: 1,
          borderColor,
          backgroundColor: Colors.whiteColor,
          overflow: "hidden",
          maxWidth: "100%",
          flexShrink: 1,
          alignSelf: "flex-start",
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={{
            paddingHorizontal: 10,
            paddingVertical: 5,
            flexGrow: 1,
            flexShrink: 1,
          }}
        >
          <TextInput
            ref={inputRef}
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor={Colors.grayColor}
            style={{
              minWidth: 0,
              flexGrow: 1,
              flexShrink: 1,
              fontSize: 12,
              fontWeight: "500",
              color: Colors.darkgrayColor,
              paddingVertical: 0,
              outlineStyle: "none",
            }}
            autoCorrect={false}
            autoCapitalize="none"
            blurOnSubmit={false}
            autoFocus={autoFocus}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onSubmitEditing={() => {
              if (canSubmit) onSubmit?.();
            }}
            onKeyDown={handleKey}
            onKeyPress={handleKey}
            maxLength={35}
          />
        </View>

        <View
          style={{
            width: 1,
            alignSelf: "stretch",
            backgroundColor: Colors.borderColor,
          }}
        />

        <TouchableOpacity
          onPress={onCancel}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 5,
            outlineStyle: "none",
            cursor: "pointer",
            opacity: 0.7,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              fontWeight: "500",
              color: withAlpha(Colors.grayColor, 0.85),
              lineHeight: 16,
              maxWidth: 84,
            }}
            numberOfLines={1}
          >
            {cancelLabel}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={canSubmit ? onSubmit : undefined}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 5,
            outlineStyle: "none",
            cursor: canSubmit ? "pointer" : "default",
            opacity: canSubmit ? 1 : 0.45,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              fontWeight: "700",
              color: Colors.darkgrayColor,
              lineHeight: 16,
              maxWidth: 84,
            }}
            numberOfLines={1}
          >
            {saveLabel}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }
);

/**
 * PredefinedAttributeLayout
 *
 * Renders predefined attributes grouped by title only (no subtitle separation).
 * Each title section has a trailing "+" pill.
 *
 * Expected data shape:
 * sections: [{
 *   key: string,
 *   title: string,
 *   groups: [{ key: string, subtitle: string, items: [{ id, name, originalName, type, subtype }] }]
 * }]
 */
const PredefinedAttributeLayout = ({
  sections = [],
  selectedModifierIds,
  hoveredModifierId = null,
  onToggleModifier,
  onAddModifier,
  onCreateModeChange,
  preventKeyboardDismissOnPress = false,
  onItemPressIn,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  const [creatingSectionKey, setCreatingSectionKey] = useState(null);
  const [draftModifierName, setDraftModifierName] = useState("");
  const createInputRef = useRef(null);

  useEffect(() => {
    if (!creatingSectionKey) return;
    const focusTimer = setTimeout(() => {
      try {
        createInputRef.current?.focus?.();
      } catch {
        // no-op
      }
    }, 30);
    return () => clearTimeout(focusTimer);
  }, [creatingSectionKey]);

  const isSelected = useCallback(
    (id) => (selectedModifierIds ? selectedModifierIds.has(id) : false),
    [selectedModifierIds]
  );

  const beginCreateForSection = useCallback((section) => {
    const sectionKey = section?.key || section?.title;
    if (!sectionKey) return;
    setCreatingSectionKey(sectionKey);
    setDraftModifierName("");
  }, []);

  const cancelCreate = useCallback(() => {
    setCreatingSectionKey(null);
    setDraftModifierName("");
  }, []);

  useEffect(() => {
    onCreateModeChange?.(
      creatingSectionKey
        ? { isCreating: true, cancel: cancelCreate, key: creatingSectionKey }
        : { isCreating: false, cancel: null, key: null }
    );
  }, [cancelCreate, creatingSectionKey, onCreateModeChange]);

  const submitCreate = useCallback(() => {
    if (!creatingSectionKey) return;
    const name = (draftModifierName || "").trim();
    if (!name) return;

    const section = (sections || []).find(
      (s) => (s?.key || s?.title) === creatingSectionKey
    );
    if (!section) return;

    // Pass first group as default (or the section can handle it)
    const firstGroup =
      Array.isArray(section?.groups) && section.groups.length > 0
        ? section.groups[0]
        : null;
    onAddModifier?.(section, firstGroup, name);
    setCreatingSectionKey(null);
    setDraftModifierName("");
  }, [creatingSectionKey, draftModifierName, sections, onAddModifier]);

  return (
    <View style={{ gap: 16 }}>
      {sections.map((section) => {
        const titleText = section.title ? t(section.title) : "";
        const groups = Array.isArray(section.groups) ? section.groups : [];

        // Flatten all items from all groups
        const allItems = groups.flatMap((group) =>
          Array.isArray(group.items) ? group.items : []
        );

        const sectionKey = section?.key || section?.title;
        const isCreatingThisSection = sectionKey === creatingSectionKey;
        const canSubmitCreate = (draftModifierName || "").trim().length > 0;

        return (
          <View key={section.key || section.title} style={{ gap: 10 }}>
            {!!titleText && (
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: Colors.grayColor,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {titleText}
              </Text>
            )}

            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
              {allItems.map((modifier) => (
                <PillButton
                  key={modifier.id}
                  isMobile={isMobile}
                  label={t(modifier.name)}
                  selected={isSelected(modifier.id)}
                  forceHover={
                    !!hoveredModifierId && modifier.id === hoveredModifierId
                  }
                  preventDefaultOnPress={preventKeyboardDismissOnPress}
                  onPressIn={() => {
                    onItemPressIn?.(modifier, section, {
                      isSelected: isSelected(modifier.id),
                    });
                  }}
                  onPress={() => onToggleModifier?.(modifier, section, null)}
                />
              ))}

              {isCreatingThisSection ? (
                <CreateModifierComposerPill
                  isMobile={isMobile}
                  inputRef={createInputRef}
                  value={draftModifierName}
                  onChangeText={setDraftModifierName}
                  placeholder={t("modifierNamePlaceholder-string")}
                  onSubmit={submitCreate}
                  onCancel={cancelCreate}
                  canSubmit={canSubmitCreate}
                  cancelLabel={t("cancel-string")}
                  saveLabel={t("save-string")}
                  autoFocus
                />
              ) : (
                <PillButton
                  label={"+"}
                  isMobile={isMobile}
                  isAdd
                  selected={false}
                  preventDefaultOnPress={preventKeyboardDismissOnPress}
                  onPress={() => beginCreateForSection(section)}
                />
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default PredefinedAttributeLayout;
